// Hook: useSelections
// Manages material selections, selected vehicle entities, and enablement flags
import { useMemo, useState, useCallback } from "react";

export default function useSelections() {
  const [materialSelections, setMaterialSelections] = useState({});
  const [savedMaterialKeys, setSavedMaterialKeys] = useState({});
  const [selectedVehicleEntities, setSelectedVehicleEntities] = useState({});

  // Memoize functions using internal state; consumers pass only the key
  const isSaveEnabled = useCallback(
    (currentMaterialKey) => {
      if (!currentMaterialKey) return false;
      const rec = materialSelections[currentMaterialKey];
      const alreadySaved = savedMaterialKeys[currentMaterialKey];
      if (!rec || alreadySaved) return false;
      return rec.vehicleIds.length === rec.requestedQuantity && rec.requestedQuantity > 0;
    },
    [materialSelections, savedMaterialKeys]
  );

  // Optional clearer alias to convey intent
  const canSave = isSaveEnabled;

  const overSelectedInfo = useCallback(
    (currentMaterialKey) => {
      if (!currentMaterialKey) return null;
      const rec = materialSelections[currentMaterialKey];
      const alreadySaved = savedMaterialKeys[currentMaterialKey];
      if (!rec || alreadySaved) return null;
      if (rec.vehicleIds.length > rec.requestedQuantity) {
        return { selected: rec.vehicleIds.length, requested: rec.requestedQuantity };
      }
      return null;
    },
    [materialSelections, savedMaterialKeys]
  );

  // Compute continue-enabled as a value based on current state
  const isContinueEnabled = useMemo(() => {
    const entries = Object.keys(materialSelections);
    if (!entries.length) return false;
    return entries.every((key) => savedMaterialKeys[key]);
  }, [materialSelections, savedMaterialKeys]);

  return {
    materialSelections,
    setMaterialSelections,
    savedMaterialKeys,
    setSavedMaterialKeys,
    selectedVehicleEntities,
    setSelectedVehicleEntities,
    isSaveEnabled,
    canSave,
    overSelectedInfo,
    isContinueEnabled,
  };
}
